import React, { useEffect, useState, useMemo, useContext } from "react";
import { useNavigate } from "react-router";
import {
  Table,
  Input,
  Button,
  Space,
  Breadcrumb,
  Typography,
  Card,
  Modal,
  Form,
  message,
} from "antd";
import { Button as CustomButton } from "../../components/ui/button";
import { format } from "date-fns";
import {
  PlusOutlined,
  SearchOutlined,
  EditOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import axios from "axios";
import {
  list_paginated_interpreters,
  update_interpreter_special_id,
} from "../../api/InterpreterApi";
import { get_interpreters_to_download_chunked } from "../../api/AdminApi";
import { sync_users } from "../../api/GeneralApi";
import { GlobalContext } from "../../App";
import { Download } from "lucide-react";

const { Title } = Typography;

const InterpretersList = () => {
  const navigate = useNavigate();
  const { setToastOpen, setToastMessage, setToastSeverity, languages } =
    useContext(GlobalContext);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [debouncedSearchText, setDebouncedSearchText] = useState("");
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [interpreters, setInterpreters] = useState([]);
  const [allInterpreters, setAllInterpreters] = useState([]);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [currentInterpreter, setCurrentInterpreter] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [form] = Form.useForm();
  const getData = async () => {
    try {
      setLoading(true);
      const response = await list_paginated_interpreters(
        pagination.current,
        pagination.pageSize,
        debouncedSearchText
      );

      // Ensure response is an array
      const interpretersData = Array.isArray(response.data.items)
        ? response.data.items
        : [];

      setAllInterpreters(interpretersData);
      setInterpreters(interpretersData);
      setPagination((prev) => ({
        ...prev,
        total: response.data.meta.total,
      }));
      setLoading(false);
    } catch (error) {
      console.error("Error fetching interpreters:", error);
      setAllInterpreters([]);
      setInterpreters([]);
      setLoading(false);
    }
  };

  const handleSyncUsers = async () => {
    try {
      const response = await sync_users();
      console.log(response);
      setToastOpen(true);
      setToastMessage("Users sync initiated.");
      setToastSeverity("success");
    } catch (error) {
      console.error("Error syncing users:", error);
      setToastOpen(true);
      setToastMessage(error.response.data.message);
      setToastSeverity("error");
    }
  };
  const onSearch = (value) => {
    setSearchText(value);
    setPagination((prev) => ({ ...prev, current: 1 }));

    if (!value.trim()) {
      setInterpreters(allInterpreters);
      return;
    }
  };

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const handleTableChange = (newPagination, filters, sorter) => {
    setPagination(newPagination);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const columns = [
    {
      title: "Interpreter Name",
      dataIndex: "intepreter_display_name",
      key: "intepreter_display_name",
      render: (text, record) => (
        <Button
          type="link"
          onClick={() =>
            navigate(`/admin/interpreters/view/${record.interpreter_id}`)
          }
        >
          {record.interpreter_first_name + " " + record.interpreter_last_name}
        </Button>
      ),
      sorter: (a, b) =>
        a.interpreter_first_name?.localeCompare(b.interpreter_first_name || ""),
    },

    {
      title: "Interpreter ID",
      dataIndex: "interpreter_special_id",
      key: "interpreter_special_id",
      render: (text, record) => {
        if (!text) {
          return (
            <span
              className="text-xs text-red-500 font-bold cursor-pointer hover:underline"
              onClick={() => handleEdit(record)}
            >
              Add interpreter ID
            </span>
          );
        } else {
          return <span>{text}</span>;
        }
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() =>
              navigate(`/admin/interpreters/edit/${record.interpreter_id}`)
            }
          >
            Edit
          </Button>
        </Space>
      ),
    },
  ];

  const handleEdit = (record) => {
    setCurrentInterpreter(record);
    form.setFieldsValue({
      interpreter_special_id: record.interpreter_special_id || "",
    });
    setIsEditModalVisible(true);
  };

  const handleEditCancel = () => {
    setIsEditModalVisible(false);
    setCurrentInterpreter(null);
    form.resetFields();
  };

  const handleEditSubmit = async () => {
    try {
      let response = await update_interpreter_special_id(
        currentInterpreter.interpreter_id,
        form.getFieldValue("interpreter_special_id")
      );
      setIsEditModalVisible(false);
      setInterpreters((prev) =>
        prev.map((interpreter) =>
          interpreter.interpreter_id === currentInterpreter.interpreter_id
            ? {
                ...interpreter,
                interpreter_special_id: form.getFieldValue(
                  "interpreter_special_id"
                ),
              }
            : interpreter
        )
      );
      setToastOpen(true);
      setToastMessage("Interpreter ID updated successfully");
      setToastSeverity("success");
    } catch (error) {
      console.error("Error updating interpreter ID:", error);
      setToastOpen(true);
      setToastMessage(error.response.data.message);
      setToastSeverity("error");
    } finally {
      setLoading(false);
    }
  };

  const downloadCSV = async () => {
    setIsDownloading(true);
    setDownloadProgress(0);

    try {
      let response = await get_interpreters_to_download_chunked((count) => {
        setDownloadProgress(count);
      });

      const data = response.data || [];

      if (!data.length) {
        setToastMessage("No data available to download.");
        setToastOpen(true);
        setToastSeverity("warning");
        return;
      }

      // Dynamically get columns from response keys
      const dynamicColumns = Object.keys(data[0]);

      // Create CSV header row
      const header = dynamicColumns.map((key) => `"${key}"`).join(",");

      // Format CSV rows
      const csvRows = data.map((row) => {
        return dynamicColumns
          .map((key) => {
            const value = row[key];
            return `"${value ? String(value).replace(/"/g, '""') : ""}"`;
          })
          .join(",");
      });

      // Combine header and rows
      const csvContent = [header, ...csvRows].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `interpreters_${format(new Date(), "yyyy-MM-dd_HH-mm")}.csv`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error downloading CSV:", error);
      setToastMessage(error);
      setToastOpen(true);
      setToastSeverity("error");
    } finally {
      setIsDownloading(false);
      setDownloadProgress(0);
    }
  };
  const handleDownloadProgress = (count) => {
    setDownloadProgress(count);
  };
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, 700);
    return () => clearTimeout(handler);
  }, [searchText]);

  useEffect(() => {
    getData();
  }, [pagination.current, pagination.pageSize, debouncedSearchText]);

  return (
    <div className="p-0">
      <Breadcrumb className="mb-6">
        <Breadcrumb.Item>Admin</Breadcrumb.Item>
        <Breadcrumb.Item>Interpreters</Breadcrumb.Item>
      </Breadcrumb>

      <div className="flex justify-between items-center mb-6">
        <Title level={3} className="mb-0">
          Interpreters
        </Title>

        <div className="flex gap-2">
          <CustomButton
            variant="outline"
            className="flex items-center gap-2 cursor-pointer"
            onClick={() => handleSyncUsers()}
          >
            <SyncOutlined className={`h-4 w-4`} />
            Sync Users
          </CustomButton>
          <div className=" w-px bg-gray-100"></div>
          <CustomButton
            variant="outline"
            className="flex items-center gap-2 cursor-pointer"
            onClick={downloadCSV}
            // disabled={isDownloading}
          >
            <Download className="h-4 w-4" />
            {isDownloading
              ? downloadProgress > 0
                ? `Downloading... (${downloadProgress} records)`
                : "Preparing download..."
              : "Download CSV"}
          </CustomButton>

          <CustomButton
            variant="outline"
            className="flex items-center gap-2 cursor-pointer border-blue-100 text-blue-600 hover:bg-blue-50"
            onClick={() => navigate("/admin/interpreters/add-tier4")}
          >
            <PlusOutlined className={`h-4 w-4`} />
            Add Tier 4 Interpreter
          </CustomButton>
        </div>
      </div>

      <Card className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <div className="w-1/3">
            <Input
              placeholder="Search interpreters..."
              prefix={<SearchOutlined />}
              onChange={(e) => onSearch(e.target.value)}
              allowClear
            />
          </div>
          <div>
            <span style={{ marginLeft: 8 }}>
              {selectedRowKeys.length > 0
                ? `Selected ${selectedRowKeys.length} items`
                : ""}
            </span>
          </div>
        </div>

        <Table
          rowSelection={rowSelection}
          columns={columns}
          dataSource={interpreters}
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `${total} records`,
            pageSizeOptions: ["10", "20", "30", "50", "100"],
            size: "default",
            position: ["bottomRight"],
            defaultPageSize: 30,
            current: pagination.current,
            pageSize: pagination.pageSize,
            onChange: (page, pageSize) => {
              setPagination((prev) => ({
                ...prev,
                current: page,
                pageSize: pageSize,
              }));
            },
            onShowSizeChange: (current, size) => {
              setPagination((prev) => ({
                ...prev,
                current: 1, // Reset to first page when changing page size
                pageSize: size,
              }));
            },
          }}
          onChange={handleTableChange}
          scroll={{ x: "max-content" }}
          rowKey="interpreter_id"
          size="small"
        />
      </Card>

      {/* Edit Interpreter ID Modal */}
      <Modal
        title="Edit Interpreter ID"
        open={isEditModalVisible}
        onCancel={handleEditCancel}
        onOk={handleEditSubmit}
        confirmLoading={loading}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="interpreter_special_id"
            label="Interpreter ID"
            rules={[
              { required: true, message: "Please enter the interpreter ID" },
            ]}
          >
            <Input placeholder="Enter interpreter ID" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default InterpretersList;
